/**
 * Manage Custom Assessment Domains — shared page for EM and SME portals.
 *
 * Allows EM and SME users to:
 * - Create new assessment domains with a category, name, and description
 * - Add/edit/delete assessment questions (individual or bulk CSV upload)
 * - Upload and manage assessment assets
 * - Activate, archive, or delete domains
 * - Download CSV template for bulk question upload
 * - Auto-activate domains when requirements are met
 */
import React, { useEffect, useState, useCallback, useRef, useMemo } from 'react';
import {
  Box, Typography, Paper, Grid, Button, TextField, Dialog,
  DialogTitle, DialogContent, DialogActions, Chip, IconButton,
  Table, TableHead, TableRow, TableCell, TableBody, Alert,
  CircularProgress, Select, MenuItem, FormControl, InputLabel,
  Stack, Tooltip, Divider, Card, CardContent, CardActions,
  Tabs, Tab, ListItemIcon, ListItemText, LinearProgress,
} from '@mui/material';
import {
  Add, Edit, Delete, Upload, CheckCircle, Archive,
  QuestionAnswer, AttachFile, Refresh, ExpandMore, ExpandLess,
  Download, CloudUpload, ErrorOutline, Shield, SmartToy,
  Code, Cloud, Business, Architecture, Category,
} from '@mui/icons-material';
import {
  fetchCustomDomains, fetchCustomDomain, createCustomDomain,
  updateCustomDomain, deleteCustomDomain, addQuestion, updateQuestion,
  deleteQuestion, bulkAddQuestions, uploadDomainAsset, deleteDomainAsset,
  updateDomainAsset, generateQuestions, indexDomainAssets,
  getAssetContent, saveAssetContent,
  CustomDomain, CustomDomainQuestion, CustomDomainAsset,
  GeneratedQuestion,
} from '../../api/customDomains';
import { fetchAssessmentDomains, AssessmentCategory } from '../../api/admin';

// ── Predefined categories with icons and descriptions ────────
const CATEGORY_OPTIONS = [
  { value: 'Security & Compliance', label: 'Security & Compliance', icon: <Shield fontSize="small" />, description: 'Cloud security, zero trust, data protection' },
  { value: 'AI & Analytics', label: 'AI & Analytics', icon: <SmartToy fontSize="small" />, description: 'AI readiness, GenAI maturity, analytics' },
  { value: 'Development & Operations', label: 'Development & Operations', icon: <Code fontSize="small" />, description: 'DevOps, DevSecOps, app modernization' },
  { value: 'Cloud Strategy & Adoption', label: 'Cloud Strategy & Adoption', icon: <Cloud fontSize="small" />, description: 'Cloud maturity, landing zones, WAF/CAF' },
  { value: 'Productivity & Collaboration', label: 'Productivity & Collaboration', icon: <Business fontSize="small" />, description: 'M365 Copilot, Power Platform, Dynamics 365' },
  { value: 'Baseline Architecture', label: 'Baseline Architecture', icon: <Architecture fontSize="small" />, description: 'Azure architecture generation' },
  { value: 'Custom', label: 'Custom Category', icon: <Category fontSize="small" />, description: 'Define your own category' },
];

// ── Available Foundry agents for assessment handling ─────────
const AGENT_OPTIONS = [
  {
    value: '', label: 'Auto (based on category)', agent: '',
    description: 'Automatically select the best agent based on the category you choose. Recommended for most use cases.',
  },
  {
    value: 'cloudshield', label: 'Security & Compliance Agent', agent: 'securitycomplianceagent',
    description: 'Cybersecurity posture, zero-trust architecture, identity & access management (IAM), data protection & privacy, regulatory compliance (SOC 2, ISO 27001, GDPR, NIST, PCI-DSS), risk assessment frameworks, security audits, vulnerability management, and incident response readiness.',
  },
  {
    value: 'genaiops', label: 'AI & Analytics Agent', agent: 'ai-analytics-agent',
    description: 'AI/ML readiness, generative AI maturity, MLOps pipelines, responsible AI practices, AI Builder assessments, data foundations & data governance, analytics strategy, business intelligence maturity, AI governance frameworks, and machine learning model lifecycle management.',
  },
  {
    value: 'devflow', label: 'DevOps & Software Engineering Agent', agent: 'devopsagent',
    description: 'Software development lifecycle (SDLC), CI/CD pipeline maturity, Infrastructure as Code (IaC), DevSecOps practices, legacy application modernisation, code quality & technical debt assessment, API management, microservices readiness, software quality assurance, release management, testing automation, and developer productivity.',
  },
  {
    value: 'adoptiq', label: 'Cloud Strategy & Migration Agent', agent: 'cloudstrategyagent',
    description: 'Cloud maturity & adoption strategy, cloud infrastructure migration (server, VM, Windows Server), database assessment & migration (SQL, PostgreSQL, Oracle to Azure), cloud storage assessment & optimisation, Azure landing zones, Well-Architected Framework (WAF), Cloud Adoption Framework (CAF), hybrid/multi-cloud planning, and data centre exit strategies.',
  },
  {
    value: 'poweradopt', label: 'Productivity & Automation Agent', agent: 'prodcollaborationagent',
    description: 'Microsoft 365 Copilot readiness, Power Platform adoption (Power Apps, Power Automate, Power BI, Power Pages), RPA & Robotic Process Automation, AI Builder integration, Dynamics 365 (Sales, Customer Service, Field Service, Contact Center), Teams collaboration, SharePoint modernisation, digital workplace transformation, citizen developer enablement, and business process automation.',
  },
  {
    value: 'blueprint', label: 'Architecture & Infrastructure Agent', agent: 'baseline-architecture-agent',
    description: 'Azure reference architecture design, infrastructure sizing & cost optimisation, high-availability & disaster recovery patterns, network topology & security design, storage architecture, compute platform selection (VMs, AKS, App Service, Functions), landing zone blueprints, and workload-specific architecture reviews.',
  },
];

const SCALE_TYPES = [
  { value: 'maturity_5', label: 'Maturity Scale (1–5)', description: '1=Initial/Ad-hoc, 2=Developing, 3=Defined, 4=Managed, 5=Optimizing' },
  { value: 'effectiveness_4', label: 'Effectiveness Scale (1–4)', description: '1=Ineffective, 2=Partially Effective, 3=Mostly Effective, 4=Fully Effective' },
  { value: 'yes_no_partial', label: 'Yes / No / Partial', description: 'Compliance check: Yes, No, or Partial' },
  { value: 'frequency', label: 'Frequency Scale', description: 'Never, Rarely, Sometimes, Always' },
  { value: 'investment', label: 'Investment Scale', description: 'None, Minimal, Moderate, Significant' },
];

const STATUS_COLORS: Record<string, 'default' | 'success' | 'warning' | 'error'> = {
  draft: 'warning',
  active: 'success',
  archived: 'default',
};

const STATUS_OPTIONS = [
  { value: '', label: 'All Statuses', description: 'Show all domains regardless of status' },
  { value: 'draft', label: 'Draft', description: 'Domains still being configured' },
  { value: 'active', label: 'Active', description: 'Live domains ready for assessment' },
  { value: 'archived', label: 'Archived', description: 'Deactivated domains, read-only' },
];

// ── CSV template / parsing helpers ──────────────────────────

const CSV_TEMPLATE_HEADER = 'question_key,question_text,scale_type,section';
const CSV_TEMPLATE_ROWS = [
  'data_governance_strategy,"How mature is your organisation\'s data governance strategy?",maturity_5,Governance & Strategy',
  'security_incident_response,"How effective is your incident response process?",effectiveness_4,Risk & Compliance',
  'cloud_migration_plan,"Has your organisation defined a cloud migration roadmap?",yes_no_partial,Cloud Strategy',
  'training_frequency,"How often does your team receive security awareness training?",frequency,Risk & Compliance',
  'ai_investment_level,"What is the current level of AI/ML investment?",investment,AI & Innovation',
];
const CSV_TEMPLATE = [CSV_TEMPLATE_HEADER, ...CSV_TEMPLATE_ROWS].join('\n');

interface CsvParseResult {
  questions: { question_key: string; question_text: string; scale_type: string; section?: string }[];
  errors: string[];
  warnings: string[];
}

/**
 * Parse and validate a CSV string for question bulk upload.
 * Returns parsed questions plus any validation errors/warnings.
 */
function parseCsvQuestions(csvText: string): CsvParseResult {
  const validScales = new Set(SCALE_TYPES.map(s => s.value));
  const lines = csvText.split(/\r?\n/).filter(line => line.trim());
  const questions: CsvParseResult['questions'] = [];
  const errors: string[] = [];
  const warnings: string[] = [];

  if (lines.length === 0) {
    errors.push('CSV file is empty');
    return { questions, errors, warnings };
  }

  // Parse header
  const header = lines[0].toLowerCase().trim();
  const hasHeader = header.includes('question_key') || header.includes('question_text');
  const startIdx = hasHeader ? 1 : 0;

  if (hasHeader) {
    const cols = header.split(',').map(c => c.trim().replace(/^"|"$/g, ''));
    const expectedCols = ['question_key', 'question_text', 'scale_type'];
    const missing = expectedCols.filter(c => !cols.includes(c));
    if (missing.length > 0) {
      errors.push(`CSV header is missing required columns: ${missing.join(', ')}`);
      return { questions, errors, warnings };
    }
  }

  if (lines.length - startIdx === 0) {
    errors.push('CSV file contains no data rows (only a header)');
    return { questions, errors, warnings };
  }

  if (lines.length - startIdx > 200) {
    errors.push(`CSV contains ${lines.length - startIdx} rows. Maximum is 200 questions per upload.`);
    return { questions, errors, warnings };
  }

  const seenKeys = new Set<string>();

  for (let i = startIdx; i < lines.length; i++) {
    const rowNum = i + 1;
    const line = lines[i].trim();
    if (!line) continue;

    // Simple CSV parse that handles quoted fields with commas
    const fields: string[] = [];
    let current = '';
    let inQuotes = false;
    for (let j = 0; j < line.length; j++) {
      const ch = line[j];
      if (ch === '"') {
        if (inQuotes && j + 1 < line.length && line[j + 1] === '"') {
          current += '"';
          j++; // skip escaped quote
        } else {
          inQuotes = !inQuotes;
        }
      } else if (ch === ',' && !inQuotes) {
        fields.push(current.trim());
        current = '';
      } else {
        current += ch;
      }
    }
    fields.push(current.trim());

    if (fields.length < 2) {
      errors.push(`Row ${rowNum}: Expected at least 2 columns (question_key, question_text), found ${fields.length}`);
      continue;
    }

    const key = fields[0].replace(/^"|"$/g, '').trim();
    const text = fields[1].replace(/^"|"$/g, '').trim();
    let scale = (fields[2] || 'maturity_5').replace(/^"|"$/g, '').trim();
    const section = (fields[3] || '').replace(/^"|"$/g, '').trim() || undefined;

    if (!key) {
      errors.push(`Row ${rowNum}: question_key is empty`);
      continue;
    }
    if (key.length > 255) {
      errors.push(`Row ${rowNum}: question_key exceeds 255 characters`);
      continue;
    }
    if (!text) {
      errors.push(`Row ${rowNum}: question_text is empty`);
      continue;
    }
    if (!validScales.has(scale)) {
      warnings.push(`Row ${rowNum}: Unknown scale_type "${scale}", defaulting to maturity_5`);
      scale = 'maturity_5';
    }
    if (seenKeys.has(key)) {
      warnings.push(`Row ${rowNum}: Duplicate question_key "${key}" — will be skipped`);
      continue;
    }

    seenKeys.add(key);
    questions.push({ question_key: key, question_text: text, scale_type: scale, section });
  }

  return { questions, errors, warnings };
}

function downloadCsvTemplate() {
  const blob = new Blob([CSV_TEMPLATE], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'assessment_questions_template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

// ── Main component ──────────────────────────────────────────

const ManageDomains: React.FC = () => {
  const [domains, setDomains] = useState<CustomDomain[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [categoryFilter, setCategoryFilter] = useState<string>('');

  // Dynamically merge hardcoded categories with categories from loaded domains
  const filterCategoryOptions = useMemo(() => {
    const knownValues = new Set(CATEGORY_OPTIONS.map(c => c.value));
    const extra: { value: string; label: string; icon: JSX.Element; description: string }[] = [];
    for (const d of domains) {
      if (d.category && !knownValues.has(d.category) && d.category !== 'Custom') {
        knownValues.add(d.category);
        extra.push({
          value: d.category,
          label: d.category,
          icon: <Category fontSize="small" />,
          description: 'Custom category',
        });
      }
    }
    // Return predefined (excluding the "Custom" meta-option) + dynamic custom categories
    return [
      ...CATEGORY_OPTIONS.filter(c => c.value !== 'Custom'),
      ...extra,
    ];
  }, [domains]);

  // Categories for the create dialog: predefined + existing custom + "Custom" at the end
  const createCategoryOptions = useMemo(() => {
    const knownValues = new Set(CATEGORY_OPTIONS.map(c => c.value));
    const extra: { value: string; label: string; icon: JSX.Element; description: string }[] = [];
    for (const d of domains) {
      if (d.category && !knownValues.has(d.category) && d.category !== 'Custom') {
        knownValues.add(d.category);
        extra.push({
          value: d.category,
          label: d.category,
          icon: <Category fontSize="small" />,
          description: 'Existing custom category',
        });
      }
    }
    // Predefined (without Custom) + discovered custom categories + Custom meta-option last
    return [
      ...CATEGORY_OPTIONS.filter(c => c.value !== 'Custom'),
      ...extra,
      ...CATEGORY_OPTIONS.filter(c => c.value === 'Custom'),
    ];
  }, [domains]);

  // Create dialog
  const [createOpen, setCreateOpen] = useState(false);
  const [newName, setNewName] = useState('');
  const [newCategory, setNewCategory] = useState('');
  const [customCategory, setCustomCategory] = useState('');
  const [newDescription, setNewDescription] = useState('');
  const [newAgentKey, setNewAgentKey] = useState('');
  const [creating, setCreating] = useState(false);

  // Detail view
  const [selectedDomain, setSelectedDomain] = useState<CustomDomain | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [detailTab, setDetailTab] = useState(0);

  // Question dialog
  const [qDialogOpen, setQDialogOpen] = useState(false);
  const [editingQuestion, setEditingQuestion] = useState<CustomDomainQuestion | null>(null);
  const [qKey, setQKey] = useState('');
  const [qText, setQText] = useState('');
  const [qScale, setQScale] = useState('maturity_5');
  const [savingQuestion, setSavingQuestion] = useState(false);

  // CSV upload state
  const [csvDialogOpen, setCsvDialogOpen] = useState(false);
  const [csvFile, setCsvFile] = useState<File | null>(null);
  const [csvParseResult, setCsvParseResult] = useState<CsvParseResult | null>(null);
  const [csvUploading, setCsvUploading] = useState(false);
  const csvInputRef = useRef<HTMLInputElement>(null);

  // Asset upload
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadDesc, setUploadDesc] = useState('');
  const [uploading, setUploading] = useState(false);

  // Asset edit (replace file / update description)
  const [editAsset, setEditAsset] = useState<CustomDomainAsset | null>(null);
  const [editAssetFile, setEditAssetFile] = useState<File | null>(null);
  const [editAssetDesc, setEditAssetDesc] = useState('');
  const [editingAsset, setEditingAsset] = useState(false);

  // Inline JSON/text content editing
  const [editAssetContent, setEditAssetContent] = useState<string | null>(null);
  const [loadingAssetContent, setLoadingAssetContent] = useState(false);
  const [savingAssetContent, setSavingAssetContent] = useState(false);
  const [assetContentError, setAssetContentError] = useState<string | null>(null);

  // AI question generation
  const [genDialogOpen, setGenDialogOpen] = useState(false);
  const [genCount, setGenCount] = useState(15);
  const [genFocusAreas, setGenFocusAreas] = useState('');
  const [genScale, setGenScale] = useState('maturity_5');
  const [generating, setGenerating] = useState(false);
  const [generatedQuestions, setGeneratedQuestions] = useState<GeneratedQuestion[]>([]);
  const [genError, setGenError] = useState<string | null>(null);

  // Asset indexing
  const [indexing, setIndexing] = useState(false);

  // Success / error messages
  const [successMsg, setSuccessMsg] = useState<string | null>(null);

  // Built-in assessments (read-only reference so every category shows its assessments)
  const [builtInCats, setBuiltInCats] = useState<AssessmentCategory[]>([]);
  const [showBuiltIn, setShowBuiltIn] = useState(false);

  useEffect(() => {
    // Non-fatal: if the current role can't read the admin tree, just hide the panel.
    fetchAssessmentDomains()
      .then(setBuiltInCats)
      .catch(() => setBuiltInCats([]));
  }, []);

  const builtInByCategory = useMemo(() => {
    // Reference built-ins have no draft/active/archived status, so only surface
    // them under a specific category AND when no status filter is applied.
    // Exclude built-ins already editable in the grid (seeded) to avoid duplicates.
    if (!categoryFilter || statusFilter) return [];
    const editableNames = new Set(domains.map((d) => d.name));
    return builtInCats
      .map((c) => ({
        name: c.name,
        items: c.sub_domains.filter((s) => !s.is_custom && !editableNames.has(s.name)),
      }))
      .filter((c) => c.items.length > 0 && c.name === categoryFilter);
  }, [builtInCats, categoryFilter, statusFilter, domains]);

  const loadDomains = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const result = await fetchCustomDomains({
        status: statusFilter || undefined,
        category: categoryFilter || undefined,
      });
      setDomains(result);
    } catch (err: any) {
      setError(err.message || 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, categoryFilter]);

  useEffect(() => { loadDomains(); }, [loadDomains]);

  // Auto-clear success message
  useEffect(() => {
    if (successMsg) {
      const t = setTimeout(() => setSuccessMsg(null), 5000);
      return () => clearTimeout(t);
    }
  }, [successMsg]);

  // ── Auto-activate check ───────────────────────────────────
  // When a domain has questions and all required fields, offer to activate
  const checkAutoActivate = useCallback(async (domain: CustomDomain) => {
    if (
      domain.status === 'draft' &&
      domain.name &&
      domain.category &&
      (domain.questions?.length ?? 0) > 0
    ) {
      const activate = window.confirm(
        `Domain "${domain.name}" now has ${domain.questions?.length} question(s) and all required fields are filled.\n\nWould you like to activate it now?\n\n(You can also activate it later from the domain card.)`
      );
      if (activate) {
        try {
          await updateCustomDomain(domain.id, { status: 'active' });
          setSuccessMsg(`Domain "${domain.name}" has been activated!`);
          loadDomains();
          return true;
        } catch (err: any) {
          setError(err.message);
        }
      }
    }
    return false;
  }, [loadDomains]);

  // ── Create domain ─────────────────────────────────────────
  const handleCreate = async () => {
    const category = newCategory === 'Custom' ? customCategory.trim() : newCategory;
    if (!newName.trim() || !category) return;
    setCreating(true);
    try {
      await createCustomDomain({
        name: newName.trim(),
        category,
        description: newDescription.trim() || undefined,
        agent_key: newAgentKey || undefined,
      });
      setSuccessMsg(`Domain "${newName.trim()}" created as draft. Add questions to activate it.`);
      setCreateOpen(false);
      setNewName('');
      setNewCategory('');
      setCustomCategory('');
      setNewDescription('');
      setNewAgentKey('');
      loadDomains();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  };

  // ── Load domain details ───────────────────────────────────
  const openDetail = async (domainId: string) => {
    setDetailLoading(true);
    setDetailTab(0);
    try {
      const full = await fetchCustomDomain(domainId);
      setSelectedDomain(full);
    } catch (err: any) {
      setError(err.message);
    } finally {
      setDetailLoading(false);
    }
  };

  const refreshDetail = async () => {
    if (!selectedDomain) return;
    const full = await fetchCustomDomain(selectedDomain.id);
    setSelectedDomain(full);
    return full;
  };

  // ── Status changes ────────────────────────────────────────
  const handleStatusChange = async (domainId: string, newStatus: string) => {
    try {
      await updateCustomDomain(domainId, { status: newStatus });
      setSuccessMsg(`Domain ${newStatus === 'active' ? 'activated' : newStatus}`);
      loadDomains();
      if (selectedDomain?.id === domainId) refreshDetail();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ── Delete domain ─────────────────────────────────────────
  const handleDelete = async (domainId: string, domainName: string) => {
    if (!window.confirm(`Delete domain "${domainName}"? This cannot be undone.`)) return;
    try {
      await deleteCustomDomain(domainId);
      setSuccessMsg(`Domain "${domainName}" deleted`);
      if (selectedDomain?.id === domainId) setSelectedDomain(null);
      loadDomains();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ── Question CRUD ─────────────────────────────────────────
  const openAddQuestion = () => {
    setEditingQuestion(null);
    setQKey('');
    setQText('');
    setQScale('maturity_5');
    setQDialogOpen(true);
  };

  const openEditQuestion = (q: CustomDomainQuestion) => {
    setEditingQuestion(q);
    setQKey(q.question_key);
    setQText(q.question_text);
    setQScale(q.scale_type);
    setQDialogOpen(true);
  };

  const saveQuestion = async () => {
    if (!selectedDomain || !qKey.trim() || !qText.trim()) return;
    setSavingQuestion(true);
    try {
      if (editingQuestion) {
        await updateQuestion(selectedDomain.id, editingQuestion.id, {
          question_text: qText.trim(),
          scale_type: qScale,
        });
        setSuccessMsg('Question updated');
      } else {
        await addQuestion(selectedDomain.id, {
          question_key: qKey.trim(),
          question_text: qText.trim(),
          scale_type: qScale,
        });
        setSuccessMsg('Question added');
      }
      setQDialogOpen(false);
      const updated = await refreshDetail();
      if (updated && !editingQuestion) {
        checkAutoActivate(updated);
      }
    } catch (err: any) {
      setError(err.message);
    } finally {
      setSavingQuestion(false);
    }
  };

  const handleDeleteQuestion = async (questionId: string) => {
    if (!selectedDomain) return;
    if (!window.confirm('Delete this question?')) return;
    try {
      await deleteQuestion(selectedDomain.id, questionId);
      setSuccessMsg('Question deleted');
      refreshDetail();
    } catch (err: any) {
      setError(err.message);
    }
  };

  // ── CSV Upload ────────────────────────────────────────────
  const openCsvDialog = () => {
    setCsvFile(null);
    setCsvParseResult(null);
    setCsvUploading(false);
    setCsvDialogOpen(true);
  };

  const handleCsvFileSelect = (file: File | null) => {
    setCsvFile(file);
    setCsvParseResult(null);
    if (!file) return;

    // Validate file type
    if (!file.name.toLowerCase().endsWith('.csv')) {
      setCsvParseResult({ questions: [], errors: ['Please select a .csv file'], warnings: [] });
      return;
    }
    if (file.size > 5 * 1024 * 1024) {
      setCsvParseResult({ questions: [], errors: ['CSV file exceeds 5 MB'], warnings: [] });
      return;
    }

    // Parse file
    const reader = new FileReader();
    reader.onload = (e) => {
      const text = e.target?.result as string;
      const result = parseCsvQuestions(text);
      setCsvParseResult(result);
    };
    reader.readAsText(file);
  };

  const handleCsvUpload = async () => {
    if (!selectedDomain || !csvParseResult || csvParseResult.questions.length === 0) return;
    setCsvUploading(true);
    try {
      const result = await bulkAddQuestions(selectedDomain.id, csvParseResult.questions);
      setSuccessMsg(`CSV uploaded: ${result.added} question(s) added, ${result.skipped} skipped`);
      setCsvDialogOpen(false);
      const updated = await refreshDetail();
      if (updated) {
        checkAutoActivate(updated);
      }
      loadDomains();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setCsvUploading(false);
    }
  };

  // ── Asset upload ──────────────────────────────────────────
  const handleUploadAsset = async () => {
    if (!selectedDomain || !uploadFile) return;
    setUploading(true);
    try {
      await uploadDomainAsset(selectedDomain.id, uploadFile, uploadDesc.trim() || undefined);
      setUploadDialogOpen(false);
      setUploadFile(null);
      setUploadDesc('');
      setSuccessMsg(`Asset "${uploadFile.name}" uploaded`);
      refreshDetail();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDeleteAsset = async (assetId: string, filename: string) => {
    if (!selectedDomain) return;
    if (!window.confirm(`Delete asset "${filename}"?`)) return;
    try {
      await deleteDomainAsset(selectedDomain.id, assetId);
      setSuccessMsg(`Asset "${filename}" deleted`);
      refreshDetail();
    } catch (err: any) {
      setError(err.message);
    }
  };

  const openEditAsset = (asset: CustomDomainAsset) => {
    setEditAsset(asset);
    setEditAssetFile(null);
    setEditAssetDesc(asset.description || '');
    setEditAssetContent(null);
    setAssetContentError(null);

    // Auto-load content for JSON/TXT files
    const ext = asset.filename.split('.').pop()?.toLowerCase() || '';
    if (['json', 'txt', 'csv'].includes(ext) && selectedDomain) {
      setLoadingAssetContent(true);
      getAssetContent(selectedDomain.id, asset.id)
        .then((res) => {
          setEditAssetContent(res.content);
        })
        .catch((err) => {
          setAssetContentError(err.message || 'Failed to load file content');
        })
        .finally(() => setLoadingAssetContent(false));
    }
  };

  const handleSaveAssetContent = async () => {
    if (!selectedDomain || !editAsset || editAssetContent === null) return;
    setSavingAssetContent(true);
    setAssetContentError(null);
    try {
      const res = await saveAssetContent(selectedDomain.id, editAsset.id, editAssetContent);
      setSuccessMsg(res.message);
      refreshDetail();
    } catch (err: any) {
      setAssetContentError(err.message || 'Failed to save content');
    } finally {
      setSavingAssetContent(false);
    }
  };

  const handleEditAsset = async () => {
    if (!selectedDomain || !editAsset) return;
    setEditingAsset(true);
    try {
      await updateDomainAsset(
        selectedDomain.id,
        editAsset.id,
        editAssetFile || undefined,
        editAssetDesc.trim(),
      );
      setEditAsset(null);
      setSuccessMsg(`Asset "${editAssetFile?.name || editAsset.filename}" updated`);
      refreshDetail();
    } catch (err: any) {
      setError(err.message);
    } finally {
      setEditingAsset(false);
    }
  };

  // ── AI question generation ─────────────────────────────────

  const openGenDialog = () => {
    setGenDialogOpen(true);
    setGeneratedQuestions([]);
    setGenError(null);
    setGenCount(15);
    setGenFocusAreas('');
    setGenScale('maturity_5');
  };

  const handleGenerate = async () => {
    if (!selectedDomain) return;
    setGenerating(true);
    setGenError(null);
    setGeneratedQuestions([]);
    try {
      const focusArr = genFocusAreas.split(',').map(s => s.trim()).filter(Boolean);
      const result = await generateQuestions(selectedDomain.id, {
        count: genCount,
        focus_areas: focusArr.length > 0 ? focusArr : undefined,
        scale_type: genScale,
      });
      setGeneratedQuestions(result.generated_questions);
    } catch (err: any) {
      setGenError(err.message || 'Question generation failed');
    } finally {
      setGenerating(false);
    }
  };

  const handleAcceptGenerated = async () => {
    if (!selectedDomain || generatedQuestions.length === 0) return;
    setCsvUploading(true);
    try {
      const questions = generatedQuestions.map((q, i) => ({
        question_key: `gen_${selectedDomain.domain_id}_${i + 1}`,
        question_text: q.question_text,
        scale_type: q.scale_type,
        section: q.section,
      }));
      await bulkAddQuestions(selectedDomain.id, questions);
      setSuccessMsg(`${questions.length} AI-generated questions added successfully`);
      setGenDialogOpen(false);
      refreshDetail();
    } catch (err: any) {
      setGenError(err.message || 'Failed to save generated questions');
    } finally {
      setCsvUploading(false);
    }
  };

  // ── Asset indexing ─────────────────────────────────────────

  const handleIndexAssets = async () => {
    if (!selectedDomain) return;
    if (!window.confirm('Index all assets into Azure AI Search? This makes them available to Foundry agents during assessments.')) return;
    setIndexing(true);
    try {
      const result = await indexDomainAssets(selectedDomain.id);
      const parts = [`${result.indexed_assets} asset(s) indexed (${result.document_chunks} chunks)`];
      if (result.skipped.length > 0) {
        parts.push(`${result.skipped.length} skipped`);
      }
      setSuccessMsg(parts.join(', '));
    } catch (err: any) {
      setError(err.message || 'Asset indexing failed');
    } finally {
      setIndexing(false);
    }
  };

  // ── Render ────────────────────────────────────────────────

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" sx={{ fontWeight: 700 }}>
          Manage Assessment Domains
        </Typography>
        <Stack direction="row" spacing={1}>
          <IconButton onClick={loadDomains} title="Refresh"><Refresh /></IconButton>
          <Button variant="contained" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Create Domain
          </Button>
        </Stack>
      </Box>

      {successMsg && <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMsg(null)}>{successMsg}</Alert>}
      {error && <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>{error}</Alert>}

      {/* Filters — improved with icons and descriptions */}
      <Stack direction="row" spacing={2} sx={{ mb: 3 }}>
        <FormControl size="small" sx={{ minWidth: 180 }}>
          <InputLabel>Status</InputLabel>
          <Select
            value={statusFilter}
            label="Status"
            onChange={(e) => setStatusFilter(e.target.value)}
            renderValue={(val) => {
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {val && <Chip label={val} size="small" color={STATUS_COLORS[val] || 'default'} sx={{ height: 20 }} />}
                  {!val && <Typography variant="body2">All Statuses</Typography>}
                </Box>
              );
            }}
          >
            {STATUS_OPTIONS.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <ListItemText
                  primary={
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {opt.value && <Chip label={opt.label} size="small" color={STATUS_COLORS[opt.value] || 'default'} sx={{ height: 20 }} />}
                      {!opt.value && <Typography variant="body2">{opt.label}</Typography>}
                    </Box>
                  }
                  secondary={opt.description}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl size="small" sx={{ minWidth: 260 }}>
          <InputLabel>Category</InputLabel>
          <Select
            value={categoryFilter}
            label="Category"
            onChange={(e) => setCategoryFilter(e.target.value)}
            renderValue={(val) => {
              if (!val) return <Typography variant="body2">All Categories</Typography>;
              const cat = filterCategoryOptions.find(c => c.value === val);
              return (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  {cat?.icon}
                  <Typography variant="body2">{cat?.label || val}</Typography>
                </Box>
              );
            }}
          >
            <MenuItem value="">
              <ListItemText primary="All Categories" secondary="Show domains from every category" secondaryTypographyProps={{ variant: 'caption' }} />
            </MenuItem>
            {filterCategoryOptions.map(opt => (
              <MenuItem key={opt.value} value={opt.value}>
                <ListItemIcon sx={{ minWidth: 32 }}>{opt.icon}</ListItemIcon>
                <ListItemText
                  primary={opt.label}
                  secondary={opt.description}
                  secondaryTypographyProps={{ variant: 'caption' }}
                />
              </MenuItem>
            ))}
          </Select>
        </FormControl>
      </Stack>

      {/* Built-in assessments (read-only reference) — ensures every category shows its assessments */}
      {builtInByCategory.length > 0 && (
        <Paper variant="outlined" sx={{ mb: 3, p: 2 }}>
          <Box
            sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', cursor: 'pointer' }}
            onClick={() => setShowBuiltIn((v) => !v)}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Category fontSize="small" color="action" />
              <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>Built-in Assessments</Typography>
              <Typography variant="caption" color="text.secondary">(reference — managed by the platform)</Typography>
            </Box>
            <IconButton size="small">{showBuiltIn ? <ExpandLess /> : <ExpandMore />}</IconButton>
          </Box>
          {showBuiltIn && (
            <Box sx={{ mt: 1.5 }}>
              {builtInByCategory.map((cat) => (
                <Box key={cat.name} sx={{ mb: 1.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    {CATEGORY_OPTIONS.find((c) => c.value === cat.name)?.icon}
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>{cat.name}</Typography>
                    <Typography variant="caption" color="text.secondary">· {cat.items.length} assessment(s)</Typography>
                  </Box>
                  <Stack direction="row" spacing={0.5} flexWrap="wrap" useFlexGap>
                    {cat.items.map((s) => (
                      <Chip key={s.domain_id} label={s.name} size="small" variant="outlined" />
                    ))}
                  </Stack>
                </Box>
              ))}
            </Box>
          )}
        </Paper>
      )}

      {loading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}><CircularProgress /></Box>
      ) : domains.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary" gutterBottom>No custom domains yet</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Create a new assessment domain to get started. You can add questions individually or upload a CSV file.
          </Typography>
          <Button variant="outlined" startIcon={<Add />} onClick={() => setCreateOpen(true)}>
            Create Your First Domain
          </Button>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {domains.map((d) => (
            <Grid item xs={12} md={6} lg={4} key={d.id}>
              <Card
                sx={{
                  cursor: 'pointer',
                  transition: 'box-shadow 0.2s',
                  '&:hover': { boxShadow: 6 },
                  border: selectedDomain?.id === d.id ? '2px solid #00338D' : '1px solid',
                  borderColor: selectedDomain?.id === d.id ? '#00338D' : 'divider',
                }}
                onClick={() => openDetail(d.id)}
              >
                <CardContent>
                  <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                    <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>{d.name}</Typography>
                    <Stack direction="row" spacing={0.5}>
                      {d.is_builtin && <Chip label="Built-in" size="small" color="info" variant="outlined" />}
                      <Chip label={d.status} size="small" color={STATUS_COLORS[d.status] || 'default'} />
                    </Stack>
                  </Box>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.5 }}>
                    {CATEGORY_OPTIONS.find(c => c.value === d.category)?.icon}
                    <Typography variant="body2" color="text.secondary">{d.category}</Typography>
                  </Box>
                  {d.description && (
                    <Typography variant="body2" sx={{ mb: 1, opacity: 0.8 }}>
                      {d.description.length > 100 ? `${d.description.slice(0, 100)}…` : d.description}
                    </Typography>
                  )}
                  <Stack direction="row" spacing={2} sx={{ mt: 1 }}>
                    <Chip icon={<QuestionAnswer />} label={`${d.question_count ?? 0} questions`} size="small" variant="outlined" />
                    <Chip icon={<AttachFile />} label={`${d.asset_count ?? 0} assets`} size="small" variant="outlined" />
                  </Stack>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Created by {d.created_by_name || 'Unknown'} · {d.created_at ? new Date(d.created_at).toLocaleDateString() : ''}
                  </Typography>
                </CardContent>
                <CardActions sx={{ justifyContent: 'flex-end', px: 2, pb: 1 }}>
                  {d.status === 'draft' && (
                    <Tooltip title={d.is_builtin ? 'Activate (serve edited questions to clients)' : 'Activate domain'}>
                      <IconButton size="small" color="success" onClick={(e) => { e.stopPropagation(); handleStatusChange(d.id, 'active'); }}>
                        <CheckCircle fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {d.status === 'active' && !d.is_builtin && (
                    <Tooltip title="Archive domain">
                      <IconButton size="small" onClick={(e) => { e.stopPropagation(); handleStatusChange(d.id, 'archived'); }}>
                        <Archive fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                  {d.status !== 'active' && !d.is_builtin && (
                    <Tooltip title="Delete domain">
                      <IconButton size="small" color="error" onClick={(e) => { e.stopPropagation(); handleDelete(d.id, d.name); }}>
                        <Delete fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  )}
                </CardActions>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* ── Domain Detail Panel ──────────────────────────────── */}
      {selectedDomain && (
        <Paper sx={{ mt: 4, p: 3 }}>
          {detailLoading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}><CircularProgress /></Box>
          ) : (
            <>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                <Box>
                  <Typography variant="h6" sx={{ fontWeight: 600 }}>{selectedDomain.name}</Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    {CATEGORY_OPTIONS.find(c => c.value === selectedDomain.category)?.icon}
                    <Typography variant="body2" color="text.secondary">{selectedDomain.category}</Typography>
                    <Chip label={selectedDomain.status} size="small" color={STATUS_COLORS[selectedDomain.status] || 'default'} />
                    <Chip
                      icon={<SmartToy fontSize="small" />}
                      label={selectedDomain.agent_key
                        ? AGENT_OPTIONS.find(a => a.value === selectedDomain.agent_key)?.label || selectedDomain.agent_key
                        : 'Auto'}
                      size="small"
                      variant="outlined"
                    />
                  </Box>
                </Box>
                <Button size="small" startIcon={<Refresh />} onClick={() => refreshDetail()}>Refresh</Button>
              </Box>

              {selectedDomain.description && (
                <Typography variant="body2" sx={{ mb: 2, fontStyle: 'italic' }}>{selectedDomain.description}</Typography>
              )}

              {/* Auto-activate hint */}
              {selectedDomain.status === 'draft' && (selectedDomain.questions?.length ?? 0) > 0 && (
                <Alert severity="info" sx={{ mb: 2 }} action={
                  <Button size="small" color="inherit" onClick={() => handleStatusChange(selectedDomain.id, 'active')}>
                    Activate Now
                  </Button>
                }>
                  This domain has {selectedDomain.questions?.length} question(s) and is ready to be activated.
                </Alert>
              )}

              <Tabs value={detailTab} onChange={(_, v) => setDetailTab(v)} sx={{ mb: 2 }}>
                <Tab label={`Questions (${selectedDomain.questions?.length ?? 0})`} />
                <Tab label={`Assets (${selectedDomain.assets?.length ?? 0})`} />
              </Tabs>

              {/* Questions Tab */}
              {detailTab === 0 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1, flexWrap: 'wrap' }}>
                    <Button size="small" variant="outlined" color="info" startIcon={<SmartToy />} onClick={openGenDialog}>
                      Generate with AI
                    </Button>
                    <Button size="small" variant="text" startIcon={<Download />} onClick={downloadCsvTemplate}>
                      Download CSV Template
                    </Button>
                    <Button size="small" startIcon={<CloudUpload />} variant="outlined" color="secondary" onClick={openCsvDialog}>
                      Upload CSV
                    </Button>
                    <Button size="small" startIcon={<Add />} variant="outlined" onClick={openAddQuestion}>
                      Add Question
                    </Button>
                  </Box>
                  {(selectedDomain.questions?.length ?? 0) === 0 ? (
                    <Paper variant="outlined" sx={{ p: 4, textAlign: 'center' }}>
                      <QuestionAnswer sx={{ fontSize: 48, color: 'text.disabled', mb: 1 }} />
                      <Typography variant="body1" gutterBottom>No questions yet</Typography>
                      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                        Add questions one by one, upload a CSV file, or let AI generate them.
                      </Typography>
                      <Stack direction="row" spacing={1} justifyContent="center" flexWrap="wrap">
                        <Button size="small" variant="outlined" color="info" startIcon={<SmartToy />} onClick={openGenDialog}>
                          Generate with AI
                        </Button>
                        <Button size="small" variant="text" startIcon={<Download />} onClick={downloadCsvTemplate}>
                          Download Template
                        </Button>
                        <Button size="small" startIcon={<CloudUpload />} variant="outlined" color="secondary" onClick={openCsvDialog}>
                          Upload CSV
                        </Button>
                        <Button size="small" startIcon={<Add />} variant="contained" onClick={openAddQuestion}>
                          Add Manually
                        </Button>
                      </Stack>
                    </Paper>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Question</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Scale</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDomain.questions?.map((q, idx) => (
                          <TableRow key={q.id}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell><code>{q.question_key}</code></TableCell>
                            <TableCell>{q.question_text}</TableCell>
                            <TableCell>
                              {q.section ? <Chip label={q.section} size="small" variant="outlined" /> : '—'}
                            </TableCell>
                            <TableCell>
                              <Chip label={SCALE_TYPES.find(s => s.value === q.scale_type)?.label || q.scale_type} size="small" />
                            </TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={() => openEditQuestion(q)}><Edit fontSize="small" /></IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteQuestion(q.id)}><Delete fontSize="small" /></IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}

              {/* Assets Tab */}
              {detailTab === 1 && (
                <Box>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 2, gap: 1 }}>
                    {(selectedDomain?.assets?.length ?? 0) > 0 && (
                      <Tooltip title="Index all assets into Azure AI Search so Foundry agents can use them during assessments">
                        <Button
                          size="small"
                          variant="outlined"
                          color="info"
                          startIcon={indexing ? <CircularProgress size={16} /> : <SmartToy />}
                          onClick={handleIndexAssets}
                          disabled={indexing}
                        >
                          {indexing ? 'Indexing...' : 'Index for AI Search'}
                        </Button>
                      </Tooltip>
                    )}
                    <Button size="small" startIcon={<Upload />} variant="outlined" onClick={() => setUploadDialogOpen(true)}>
                      Upload Asset
                    </Button>
                  </Box>
                  {(selectedDomain.assets?.length ?? 0) === 0 ? (
                    <Alert severity="info">No assets uploaded. Upload PDFs, Excel files, or other reference materials.</Alert>
                  ) : (
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>Filename</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Size</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Description</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Uploaded By</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Date</TableCell>
                          <TableCell sx={{ fontWeight: 600 }} align="right">Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedDomain.assets?.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>{a.filename}</TableCell>
                            <TableCell>{a.file_size_bytes ? `${(a.file_size_bytes / 1024).toFixed(1)} KB` : '—'}</TableCell>
                            <TableCell>{a.description || '—'}</TableCell>
                            <TableCell>{a.uploaded_by_name || 'Unknown'}</TableCell>
                            <TableCell>{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" color="primary" onClick={() => openEditAsset(a)}>
                                <Edit fontSize="small" />
                              </IconButton>
                              <IconButton size="small" color="error" onClick={() => handleDeleteAsset(a.id, a.filename)}>
                                <Delete fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  )}
                </Box>
              )}
            </>
          )}
        </Paper>
      )}

      {/* ── Create Domain Dialog ─────────────────────────────── */}
      <Dialog open={createOpen} onClose={() => setCreateOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Create Assessment Domain</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Domain Name"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              fullWidth
              required
              placeholder="e.g. Data Governance Maturity"
              inputProps={{ maxLength: 255 }}
            />
            <FormControl fullWidth required>
              <InputLabel>Category</InputLabel>
              <Select
                value={newCategory}
                label="Category"
                onChange={(e) => setNewCategory(e.target.value)}
                renderValue={(val) => {
                  const cat = createCategoryOptions.find(c => c.value === val);
                  return (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      {cat?.icon}
                      <Typography variant="body2">{cat?.label || val}</Typography>
                    </Box>
                  );
                }}
              >
                {createCategoryOptions.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <ListItemIcon sx={{ minWidth: 32 }}>{opt.icon}</ListItemIcon>
                    <ListItemText
                      primary={opt.label}
                      secondary={opt.description}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {newCategory === 'Custom' && (
              <TextField
                label="Custom Category Name"
                value={customCategory}
                onChange={(e) => setCustomCategory(e.target.value)}
                fullWidth
                required
                inputProps={{ maxLength: 255 }}
              />
            )}
            <TextField
              label="Description (optional)"
              value={newDescription}
              onChange={(e) => setNewDescription(e.target.value)}
              fullWidth
              multiline
              rows={3}
              placeholder="Brief description of this assessment domain"
            />
            <FormControl fullWidth>
              <InputLabel>AI Agent</InputLabel>
              <Select
                value={newAgentKey}
                label="AI Agent"
                onChange={(e) => setNewAgentKey(e.target.value)}
              >
                {AGENT_OPTIONS.map(opt => (
                  <MenuItem key={opt.value} value={opt.value}>
                    <ListItemText
                      primary={opt.label}
                      secondary={opt.description}
                      secondaryTypographyProps={{ variant: 'caption' }}
                    />
                  </MenuItem>
                ))}
              </Select>
              <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                {newAgentKey
                  ? `Foundry agent: ${AGENT_OPTIONS.find(a => a.value === newAgentKey)?.agent || newAgentKey}`
                  : 'Agent will be selected automatically based on the category'}
              </Typography>
            </FormControl>
            <Alert severity="info" variant="outlined" sx={{ fontSize: '0.8rem' }}>
              New domains start as <strong>Draft</strong>. Once you add questions, you'll be prompted to activate the domain.
              You can also activate it manually at any time.
            </Alert>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCreateOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCreate}
            disabled={creating || !newName.trim() || (!newCategory || (newCategory === 'Custom' && !customCategory.trim()))}
          >
            {creating ? <CircularProgress size={20} /> : 'Create'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Question Dialog ──────────────────────────────────── */}
      <Dialog open={qDialogOpen} onClose={() => setQDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>{editingQuestion ? 'Edit Question' : 'Add Question'}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <TextField
              label="Question Key"
              value={qKey}
              onChange={(e) => setQKey(e.target.value)}
              fullWidth
              required
              disabled={!!editingQuestion}
              placeholder="e.g. data_governance_strategy"
              helperText="Unique identifier for this question (lowercase, underscores)"
              inputProps={{ maxLength: 255 }}
            />
            <TextField
              label="Question Text"
              value={qText}
              onChange={(e) => setQText(e.target.value)}
              fullWidth
              required
              multiline
              rows={3}
              placeholder="How mature is your organisation's data governance strategy?"
            />
            <FormControl fullWidth>
              <InputLabel>Scale Type</InputLabel>
              <Select value={qScale} label="Scale Type" onChange={(e) => setQScale(e.target.value)}>
                {SCALE_TYPES.map(s => (
                  <MenuItem key={s.value} value={s.value}>{s.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setQDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={saveQuestion}
            disabled={savingQuestion || !qKey.trim() || !qText.trim()}
          >
            {savingQuestion ? <CircularProgress size={20} /> : (editingQuestion ? 'Save' : 'Add')}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── CSV Upload Dialog ────────────────────────────────── */}
      <Dialog open={csvDialogOpen} onClose={() => setCsvDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>Upload Questions from CSV</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            {/* Instructions */}
            <Alert severity="info" variant="outlined">
              <Typography variant="body2" sx={{ mb: 1, fontWeight: 600 }}>CSV Format Requirements:</Typography>
              <Typography variant="body2" component="div">
                <ul style={{ margin: 0, paddingLeft: 20 }}>
                  <li><strong>Header row</strong> (required): <code>question_key,question_text,scale_type,section</code></li>
                  <li><strong>question_key</strong>: Unique identifier (e.g. <code>data_governance_strategy</code>)</li>
                  <li><strong>question_text</strong>: The full question (wrap in quotes if it contains commas)</li>
                  <li><strong>scale_type</strong>: One of <code>maturity_5</code>, <code>effectiveness_4</code>, <code>yes_no_partial</code>, <code>frequency</code>, <code>investment</code></li>
                  <li><strong>section</strong> (optional): Module/section heading to group related questions (e.g. <code>Governance &amp; Strategy</code>)</li>
                </ul>
              </Typography>
              <Button size="small" startIcon={<Download />} onClick={downloadCsvTemplate} sx={{ mt: 1 }}>
                Download Example Template
              </Button>
            </Alert>

            {/* File selector */}
            <Box>
              <Button variant="outlined" component="label" startIcon={<CloudUpload />} fullWidth sx={{ py: 2, borderStyle: 'dashed' }}>
                {csvFile ? csvFile.name : 'Select CSV File'}
                <input
                  ref={csvInputRef}
                  type="file"
                  accept=".csv"
                  hidden
                  onChange={(e) => handleCsvFileSelect(e.target.files?.[0] || null)}
                />
              </Button>
            </Box>

            {/* Validation results */}
            {csvParseResult && (
              <Box>
                {csvParseResult.errors.length > 0 && (
                  <Alert severity="error" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Validation Errors ({csvParseResult.errors.length}):
                    </Typography>
                    {csvParseResult.errors.map((err, i) => (
                      <Typography key={i} variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                        <ErrorOutline fontSize="inherit" /> {err}
                      </Typography>
                    ))}
                  </Alert>
                )}

                {csvParseResult.warnings.length > 0 && (
                  <Alert severity="warning" sx={{ mb: 1 }}>
                    <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.5 }}>
                      Warnings ({csvParseResult.warnings.length}):
                    </Typography>
                    {csvParseResult.warnings.map((w, i) => (
                      <Typography key={i} variant="body2">{w}</Typography>
                    ))}
                  </Alert>
                )}

                {csvParseResult.errors.length === 0 && csvParseResult.questions.length > 0 && (
                  <Alert severity="success">
                    <Typography variant="body2" sx={{ fontWeight: 600 }}>
                      Ready to import {csvParseResult.questions.length} question(s)
                    </Typography>
                  </Alert>
                )}

                {/* Preview table */}
                {csvParseResult.questions.length > 0 && (
                  <Box sx={{ mt: 1, maxHeight: 300, overflow: 'auto' }}>
                    <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: 'block' }}>
                      Preview (first {Math.min(csvParseResult.questions.length, 10)} of {csvParseResult.questions.length}):
                    </Typography>
                    <Table size="small">
                      <TableHead>
                        <TableRow>
                          <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Key</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Question</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>Scale</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {csvParseResult.questions.slice(0, 10).map((q, idx) => (
                          <TableRow key={idx}>
                            <TableCell>{idx + 1}</TableCell>
                            <TableCell><code>{q.question_key}</code></TableCell>
                            <TableCell>{q.question_text.length > 80 ? `${q.question_text.slice(0, 80)}…` : q.question_text}</TableCell>
                            <TableCell>{q.section ? <Chip label={q.section} size="small" variant="outlined" /> : '—'}</TableCell>
                            <TableCell><Chip label={SCALE_TYPES.find(s => s.value === q.scale_type)?.label || q.scale_type} size="small" /></TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                    {csvParseResult.questions.length > 10 && (
                      <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                        … and {csvParseResult.questions.length - 10} more
                      </Typography>
                    )}
                  </Box>
                )}
              </Box>
            )}

            {csvUploading && <LinearProgress />}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCsvDialogOpen(false)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleCsvUpload}
            disabled={csvUploading || !csvParseResult || csvParseResult.errors.length > 0 || csvParseResult.questions.length === 0}
            startIcon={csvUploading ? <CircularProgress size={16} /> : <CloudUpload />}
          >
            {csvUploading ? 'Uploading…' : `Import ${csvParseResult?.questions.length ?? 0} Questions`}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Asset Upload Dialog ──────────────────────────────── */}
      <Dialog open={uploadDialogOpen} onClose={() => setUploadDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Upload Asset</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" startIcon={<Upload />}>
              {uploadFile ? uploadFile.name : 'Select File'}
              <input type="file" hidden onChange={(e) => setUploadFile(e.target.files?.[0] || null)} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Allowed: PDF, DOCX, XLSX, PPTX, CSV, JSON, TXT, PNG, JPG (max 50 MB)
            </Typography>
            <TextField
              label="Description (optional)"
              value={uploadDesc}
              onChange={(e) => setUploadDesc(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setUploadDialogOpen(false); setUploadFile(null); setUploadDesc(''); }}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleUploadAsset}
            disabled={uploading || !uploadFile}
          >
            {uploading ? <CircularProgress size={20} /> : 'Upload'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── Edit Asset Dialog ──────────────────────────────── */}
      <Dialog open={!!editAsset} onClose={() => setEditAsset(null)} maxWidth="md" fullWidth>
        <DialogTitle>Edit Asset — {editAsset?.filename}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ mt: 1 }}>
            <Button variant="outlined" component="label" startIcon={<Upload />}>
              {editAssetFile ? editAssetFile.name : 'Replace File (optional)'}
              <input type="file" hidden onChange={(e) => setEditAssetFile(e.target.files?.[0] || null)} />
            </Button>
            <Typography variant="caption" color="text.secondary">
              Leave blank to keep the current file. Only the description will be updated.
            </Typography>
            <TextField
              label="Description"
              value={editAssetDesc}
              onChange={(e) => setEditAssetDesc(e.target.value)}
              fullWidth
              multiline
              rows={2}
            />

            {/* Inline content editor for JSON/TXT files */}
            {editAsset && ['json', 'txt', 'csv'].includes(editAsset.filename.split('.').pop()?.toLowerCase() || '') && (
              <>
                <Divider sx={{ my: 1 }} />
                <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
                  Edit File Content
                </Typography>
                {loadingAssetContent && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <CircularProgress size={18} />
                    <Typography variant="body2" color="text.secondary">Loading file content…</Typography>
                  </Box>
                )}
                {assetContentError && (
                  <Alert severity="error" onClose={() => setAssetContentError(null)}>{assetContentError}</Alert>
                )}
                {editAssetContent !== null && (
                  <>
                    <TextField
                      fullWidth
                      multiline
                      rows={16}
                      value={editAssetContent}
                      onChange={(e) => setEditAssetContent(e.target.value)}
                      InputProps={{
                        sx: { fontFamily: 'monospace', fontSize: 12 },
                      }}
                      helperText={
                        editAsset.filename.endsWith('.json')
                          ? 'Edit the JSON content directly. The content will be validated before saving.'
                          : 'Edit the file content directly.'
                      }
                    />
                    <Button
                      variant="contained"
                      size="small"
                      onClick={handleSaveAssetContent}
                      disabled={savingAssetContent}
                      startIcon={savingAssetContent ? <CircularProgress size={16} color="inherit" /> : undefined}
                      sx={{ alignSelf: 'flex-end' }}
                    >
                      {savingAssetContent ? 'Saving Content…' : 'Save Content'}
                    </Button>
                  </>
                )}
              </>
            )}
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setEditAsset(null)}>Cancel</Button>
          <Button
            variant="contained"
            onClick={handleEditAsset}
            disabled={editingAsset}
          >
            {editingAsset ? <CircularProgress size={20} /> : 'Save'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* ── AI Question Generation Dialog ──────────────────── */}
      <Dialog open={genDialogOpen} onClose={() => setGenDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <SmartToy color="info" />
            Generate Questions with AI
          </Box>
        </DialogTitle>
        <DialogContent>
          {!generatedQuestions.length && !generating && (
            <Stack spacing={2} sx={{ mt: 1 }}>
              <Alert severity="info" variant="outlined">
                AI-powered question generation for <strong>{selectedDomain?.name}</strong>.
                Questions will be tailored to the <strong>{selectedDomain?.category}</strong> category
                using your domain description and any uploaded reference assets. Review and edit before accepting.
              </Alert>
              <TextField
                label="Number of questions"
                type="number"
                value={genCount}
                onChange={(e) => setGenCount(Math.max(1, parseInt(e.target.value) || 1))}
                inputProps={{ min: 1 }}
                size="small"
              />
              <FormControl size="small" fullWidth>
                <InputLabel>Preferred Scale</InputLabel>
                <Select value={genScale} label="Preferred Scale" onChange={(e) => setGenScale(e.target.value)}>
                  {SCALE_TYPES.map(s => (
                    <MenuItem key={s.value} value={s.value}>
                      <Box>
                        <Typography variant="body2">{s.label}</Typography>
                        <Typography variant="caption" color="text.secondary">{s.description}</Typography>
                      </Box>
                    </MenuItem>
                  ))}
                </Select>
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, ml: 1 }}>
                  {SCALE_TYPES.find(s => s.value === genScale)?.description}
                </Typography>
              </FormControl>
              <TextField
                label="Focus areas (comma-separated, optional)"
                value={genFocusAreas}
                onChange={(e) => setGenFocusAreas(e.target.value)}
                placeholder="e.g. data governance, security policies, team skills, incident response, cloud migration readiness"
                size="small"
                multiline
                minRows={2}
                helperText="Specify topics to focus on, or leave blank for general coverage. Examples: risk management, compliance frameworks, access control, AI governance, change management, disaster recovery"
              />
            </Stack>
          )}

          {generating && (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <CircularProgress />
              <Typography variant="body2" sx={{ mt: 2 }}>
                AI agent is generating questions for "{selectedDomain?.name}"...
              </Typography>
            </Box>
          )}

          {genError && <Alert severity="error" sx={{ mt: 2 }}>{genError}</Alert>}

          {generatedQuestions.length > 0 && (
            <Box sx={{ mt: 1 }}>
              <Alert severity="success" sx={{ mb: 2 }}>
                {generatedQuestions.length} question(s) generated. Review below, then click "Accept All" to add them.
              </Alert>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell sx={{ fontWeight: 600 }}>#</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Question</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Section</TableCell>
                    <TableCell sx={{ fontWeight: 600 }}>Scale</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {generatedQuestions.map((q, i) => (
                    <TableRow key={i}>
                      <TableCell>{q.sort_order}</TableCell>
                      <TableCell>{q.question_text}</TableCell>
                      <TableCell>
                        <Chip label={q.section || 'General'} size="small" variant="outlined" />
                      </TableCell>
                      <TableCell>
                        <Chip label={SCALE_TYPES.find(s => s.value === q.scale_type)?.label || q.scale_type} size="small" />
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGenDialogOpen(false)}>Cancel</Button>
          {generatedQuestions.length === 0 ? (
            <Button variant="contained" color="info" onClick={handleGenerate} disabled={generating}>
              {generating ? 'Generating...' : 'Generate'}
            </Button>
          ) : (
            <>
              <Button onClick={() => { setGeneratedQuestions([]); setGenError(null); }}>
                Regenerate
              </Button>
              <Button variant="contained" onClick={handleAcceptGenerated} disabled={csvUploading}>
                {csvUploading ? <CircularProgress size={20} /> : `Accept All (${generatedQuestions.length})`}
              </Button>
            </>
          )}
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ManageDomains;
